import React, { useState, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { HiSearch, HiX } from 'react-icons/hi';

const AddProjectModal = ({
    show,
    onHide,
    todosProjetosDisponiveis = [],
    projetosMap = {},  // Agora recebe diretamente o dicionário {id: nome}
    projetosJaIncluidos = [],
    onConfirm
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProjects, setSelectedProjects] = useState([]);

    // Filtrar projetos disponíveis para adicionar (remover os que já estão incluídos)
    const projetosParaAdicionar = useMemo(() => {
        return todosProjetosDisponiveis.filter(
            projectId => !projetosJaIncluidos.includes(projectId)
        );
    }, [todosProjetosDisponiveis, projetosJaIncluidos]);

    // Filtrar baseado no termo de busca
    const projetosFiltrados = useMemo(() => {
        if (!searchTerm.trim()) return projetosParaAdicionar;

        const term = searchTerm.toLowerCase();
        return projetosParaAdicionar.filter(projectId => {
            const nome = projetosMap[projectId] || projectId;
            return String(projectId).toLowerCase().includes(term) ||
                   String(nome).toLowerCase().includes(term);
        });
    }, [searchTerm, projetosParaAdicionar, projetosMap]);

    const handleSelectProject = (projectId) => {
        setSelectedProjects(prev => {
            if (prev.includes(projectId)) {
                return prev.filter(id => id !== projectId);
            } else {
                return [...prev, projectId];
            }
        });
    };

    const handleSelectAll = () => {
        setSelectedProjects(projetosFiltrados);
    };

    const handleDeselectAll = () => {
        setSelectedProjects([]);
    };

    const handleConfirm = () => {
        onConfirm(selectedProjects);
        setSelectedProjects([]);
        setSearchTerm('');
        onHide();
    };

    const handleClose = () => {
        setSelectedProjects([]);
        setSearchTerm('');
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton className="bg-gradient-to-r from-slate-700 to-slate-600 text-white px-6 py-4 flex items-center">
                <Modal.Title className="text-gray-800 font-semibold flex items-center">
                    {/* <HiTrendingUp className="w-5 h-5 text-blue-600 mr-2" /> */}
                    Adicionar Projetos
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '30px' }}>

                {/* Barra de busca */}
                <div className="relative mb-4">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <HiSearch className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Buscar projeto por INDEX (ex: EV-00003, EV-00006...)"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                            <HiX className="h-5 w-5" />
                        </button>
                    )}
                </div>

                {/* Ações de seleção */}
                {projetosFiltrados.length > 0 && (
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-gray-600">
                            {projetosFiltrados.length} projeto(s) {searchTerm ? 'encontrado(s)' : 'disponível(is)'}
                        </span>
                        <div className="flex space-x-2">
                            <button
                                onClick={handleSelectAll}
                                className="text-xs px-3 py-1.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors font-medium"
                            >
                                Selecionar {searchTerm ? 'resultados' : 'todos'}
                            </button>
                            <button
                                onClick={handleDeselectAll}
                                className="text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors font-medium"
                            >
                                Limpar seleção
                            </button>
                        </div>
                    </div>
                )}

                {/* Lista de projetos */}
                <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                    {projetosFiltrados.length > 0 ? (
                        projetosFiltrados.map((projectId) => {
                            const isSelected = selectedProjects.includes(projectId);
                            return (
                                <div
                                    key={projectId}
                                    className={`flex items-center space-x-3 p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                                    onClick={() => handleSelectProject(projectId)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => { }} // handled by parent click
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4 flex-shrink-0"
                                    />
                                    <span className="text-sm text-gray-700 font-medium flex-1 break-words">
                                        {projectId} - {projetosMap[projectId] || projectId}
                                    </span>
                                    {isSelected && (
                                        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded ml-auto flex-shrink-0">
                                            Selecionado
                                        </span>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-center text-gray-500 py-8">
                            <p className="text-sm">
                                {searchTerm
                                    ? `Nenhum projeto encontrado para "${searchTerm}"`
                                    : 'Todos os projetos já foram adicionados à análise'}
                            </p>
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer style={{ borderTop: '1px solid #dee2e6', padding: '15px 30px' }}>
                <Button
                    variant="secondary"
                    onClick={handleClose}
                    style={{
                            backgroundColor: '#dc3545',
                            borderColor: '#dc3545',
                            padding: '10px 30px',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            minWidth: '100px'
                        }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    onClick={handleConfirm}
                    disabled={selectedProjects.length === 0}
                    style={{
                        backgroundColor: '#003B6B',
                        borderColor: '#003B6B',
                        padding: '10px 25px',
                        fontWeight: 'bold',
                        borderRadius: '8px',
                        opacity: selectedProjects.length === 0 ? 0.6 : 1
                    }}
                >
                    Adicionar {selectedProjects.length > 0 && `(${selectedProjects.length})`}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddProjectModal;
